// /frontend/src/components/admin/ProductForm.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { productService } from '../../services/productService';
import { categoryService } from '../../services/categoryService';
import AdminLayout from '../../components/layout/AdminLayout';
import { FaUpload, FaTrash, FaPlus, FaMinus } from 'react-icons/fa';

const ProductForm = ({ isEditing = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const categories = [
    { _id: '1-bedroom', name: '1-bedroom' },
    { _id: '2-bedroom', name: '2-bedroom' },
    { _id: '3-bedroom', name: '3-bedroom' }
  ];
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    productCode: '',
    price: '',
    salePrice: '',
    category: '',
    stock: '',
    images: [],
    isOnSale: false,
    isNewArrival: false,
    isBestSeller: false,
    features: ['']
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreview, setImagePreview] = useState([]);
  
  // Fetch product data if editing
  useEffect(() => {    
    if (isEditing) {
      const fetchProduct = async () => {
        try {
          setLoading(true);
          const productData = await productService.getProductById(id);
          
          setFormData({
            name: productData.name,
            description: productData.description,
            price: productData.price,
            salePrice: productData.salePrice || '',
            category: productData.category,
            stock: productData.stock,
            images: productData.images || [],
            isOnSale: productData.isOnSale || false,
            isNewArrival: productData.isNewArrival || false,
            isBestSeller: productData.isBestSeller || false,
            features: productData.features || ['']
          });
          
          // Set image previews from existing images
          setImagePreview(productData.images || []);
          
          setLoading(false);
        } catch (err) {
          setError(err.message || 'Failed to fetch product data');
          setLoading(false);
        }
      };
      
      fetchProduct();
    }
  }, [id, isEditing]);
  
  const validateForm = () => {
    const errors = {};
    
    if (!formData.productCode.trim()) {
      errors.productCode = 'Product code is required';
    }

    if (!formData.name.trim()) {
      errors.name = 'Product name is required';
    }
    
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }
    
    if (!formData.price) {
      errors.price = 'Price is required';
    } else if (isNaN(formData.price) || Number(formData.price) <= 0) {
      errors.price = 'Price must be a positive number';
    }
    
    if (formData.salePrice && (isNaN(formData.salePrice) || Number(formData.salePrice) <= 0)) {
      errors.salePrice = 'Sale price must be a positive number';
    }
    
    if (formData.salePrice && Number(formData.salePrice) >= Number(formData.price)) {
      errors.salePrice = 'Sale price must be less than regular price';
    }
    
    if (!formData.category) {
      errors.category = 'Category is required';
    }
    
    if (!formData.stock) {
      errors.stock = 'Stock quantity is required';
    } else if (isNaN(formData.stock) || Number(formData.stock) < 0) {
      errors.stock = 'Stock must be a non-negative number';
    }
    
    if (!isEditing && formData.images.length === 0 && imageFiles.length === 0) {
      errors.images = 'At least one product image is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleFeatureChange = (index, value) => {
    const updatedFeatures = [...formData.features];
    updatedFeatures[index] = value;
    
    setFormData((prev) => ({
      ...prev,
      features: updatedFeatures
    }));
  };
  
  const addFeature = () => {
    setFormData((prev) => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };
  
  const removeFeature = (index) => {
    const updatedFeatures = [...formData.features];
    updatedFeatures.splice(index, 1);
    
    setFormData((prev) => ({
      ...prev,
      features: updatedFeatures
    }));
  };
  
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImageFiles((prevFiles) => [...prevFiles, ...files]);
    
    // Generate previews
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setImagePreview((prevPreviews) => [...prevPreviews, ...newPreviews]);
  };
  
  const removeImage = (index) => {
    // If this is an existing image (from the server)
    if (index < formData.images.length) {
      const updatedImages = [...formData.images];
      updatedImages.splice(index, 1);
      
      setFormData((prev) => ({
        ...prev,
        images: updatedImages
      }));
      
      setImagePreview((prev) => {
        const updated = [...prev];
        updated.splice(index, 1);
        return updated;
      });
    } else {
      // If this is a new image (not yet uploaded)
      const newIndex = index - formData.images.length;
      
      setImageFiles((prev) => {
        const updated = [...prev];
        updated.splice(newIndex, 1);
        return updated;
      });
      
      setImagePreview((prev) => {
        const updated = [...prev];
        updated.splice(index, 1);
        return updated;
      });
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      try {
        setLoading(true);
        
        // Create a FormData object for file uploads
        const productFormData = new FormData();
        
        // Add text fields
        Object.keys(formData).forEach(key => {
          if (key === 'images' || key === 'features') return;
          productFormData.append(key, formData[key]);
        });
        
        // Add features as JSON
        productFormData.append('features', JSON.stringify(formData.features.filter(f => f.trim())));
        
        // Add existing images
        productFormData.append('existingImages', JSON.stringify(formData.images));
        
        // Add new image files
        imageFiles.forEach(file => {
          productFormData.append('images', file);
        });
        
        if (isEditing) {
          await productService.updateProduct(id, productFormData);
        } else {
          await productService.createProduct(productFormData);
        }
        
        setLoading(false);
        navigate('/admin/products');
      } catch (err) {
        setError(err.message || `Failed to ${isEditing ? 'update' : 'create'} product`);
        setLoading(false);
      }
    }
  };
  
  if (loading && isEditing) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-800"></div>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-800">
            {isEditing ? 'Edit Product' : 'Create New Product'}
          </h1>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-md mb-6">
            <p>{error}</p>
          </div>
        )}
        
        <div className="bg-white shadow-md rounded-lg p-6">
          <form onSubmit={handleSubmit}>
            {/* Basic Info */}
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-700 mb-4">Basic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="mb-4">
                  <label htmlFor="productCode" className="block text-sm font-medium text-gray-700 mb-1">
                    Product Code
                  </label>
                  <input
                    type="text"
                    id="productCode"
                    name="productCode"
                    value={formData.productCode}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                      formErrors.productCode ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="e.g. ST02, DC001"
                  />
                  {formErrors.productCode && (
                    <p className="text-red-600 text-xs mt-1">{formErrors.productCode}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Product Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                      formErrors.name ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.name && (
                    <p className="text-red-600 text-xs mt-1">{formErrors.name}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                      formErrors.category ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category._id} value={category._id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {formErrors.category && (
                    <p className="text-red-600 text-xs mt-1">{formErrors.category}</p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Description */}
            <div className="mb-6">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="4"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                  formErrors.description ? 'border-red-300' : 'border-gray-300'
                }`}
              ></textarea>
              {formErrors.description && (
                <p className="text-red-600 text-xs mt-1">{formErrors.description}</p>
              )}
            </div>
            
            {/* Pricing */}
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-700 mb-4">Pricing & Inventory</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                    Regular Price
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      $
                    </span>
                    <input
                      type="number"
                      id="price"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                      className={`w-full pl-7 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                        formErrors.price ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {formErrors.price && (
                    <p className="text-red-600 text-xs mt-1">{formErrors.price}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="salePrice" className="block text-sm font-medium text-gray-700 mb-1">
                    Sale Price (optional)
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      $
                    </span>
                    <input
                      type="number"
                      id="salePrice"
                      name="salePrice"
                      value={formData.salePrice}
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                      className={`w-full pl-7 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                        formErrors.salePrice ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {formErrors.salePrice && (
                    <p className="text-red-600 text-xs mt-1">{formErrors.salePrice}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="stock" className="block text-sm font-medium text-gray-700 mb-1">
                    Stock Quantity
                  </label>
                  <input
                    type="number"
                    id="stock"
                    name="stock"
                    value={formData.stock}
                    onChange={handleChange}
                    min="0"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                      formErrors.stock ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.stock && (
                    <p className="text-red-600 text-xs mt-1">{formErrors.stock}</p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Product Status */}
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-700 mb-4">Product Status</h2>
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isOnSale"
                    name="isOnSale"
                    checked={formData.isOnSale}
                    onChange={handleChange}
                    className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="isOnSale" className="ml-2 text-sm text-gray-700">
                    On Sale
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isNewArrival"
                    name="isNewArrival"
                    checked={formData.isNewArrival}
                    onChange={handleChange}
                    className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="isNewArrival" className="ml-2 text-sm text-gray-700">
                    New Arrival
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isBestSeller"
                    name="isBestSeller"
                    checked={formData.isBestSeller}
                    onChange={handleChange}
                    className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="isBestSeller" className="ml-2 text-sm text-gray-700">
                    Best Seller
                  </label>
                </div>
              </div>
            </div>
            
            {/* Product Features */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-medium text-gray-700">Product Features</h2>
                <button 
                  type="button" 
                  onClick={addFeature}
                  className="text-sm text-teal-600 hover:text-teal-800 flex items-center"
                >
                  <FaPlus className="mr-1" /> Add Feature
                </button>
              </div>
              
              {formData.features.map((feature, index) => (
                <div key={index} className="flex items-center mb-2">
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => handleFeatureChange(index, e.target.value)}
                    placeholder="Enter product feature"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  {formData.features.length > 1 && (
                    <button 
                      type="button" 
                      onClick={() => removeFeature(index)}
                      className="ml-2 text-red-600 hover:text-red-800"
                    >
                      <FaMinus />
                    </button>
                  )}
                </div>
              ))}
            </div>
            
            {/* Product Images */}
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-700 mb-4">Product Images</h2>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Images
                </label>
                
                <div className="flex items-center">
                  <label className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
                    <FaUpload className="mr-2" />
                    Select Images
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageChange}
                      className="sr-only"
                    />
                  </label>
                  <span className="ml-3 text-sm text-gray-500">
                    You can upload multiple images
                  </span>
                </div>
                
                {formErrors.images && (
                  <p className="text-red-600 text-xs mt-1">{formErrors.images}</p>
                )}
              </div>
              
              {/* Image previews */}
              {imagePreview.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
                  {imagePreview.map((src, index) => (
                    <div key={index} className="relative group">
                      <img 
                        src={src} 
                        alt={`Preview ${index + 1}`} 
                        className="h-32 w-full object-cover rounded-md"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <FaTrash className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3 mt-8">
              <button
                type="button"
                onClick={() => navigate('/admin/products')}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`px-4 py-2 rounded-md text-white ${
                  loading
                    ? 'bg-teal-600 cursor-wait'
                    : 'bg-teal-600 hover:bg-teal-700'
                }`}
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    {isEditing ? 'Saving...' : 'Creating...'}
                  </div>
                ) : (
                  isEditing ? 'Save Changes' : 'Create Product'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ProductForm;